import { useEffect, useRef } from 'react';
import { FormActionButtons } from '@/components/layout/form-action-buttons';
import { FormHeaderWithStatus } from '@/components/layout/form-header-with-status';
import { Card } from '@/components/ui/card';
import {
	Form,
	FormControl,
	FormDescription,
	FormField,
	FormItem,
	FormLabel,
	FormMessage
} from '@/components/ui/form';
import { Input } from '@/components/ui/input';
import { FormSwitch } from '@/components/ui/form-switch';
import { SentryDropdown } from '@/components/ui/sentry-dropdown';
import { Separator } from '@/components/ui/separator';
import type { UseFormReturn } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

export interface RoleOption {
	value: string;
	label: string;
	description?: string | null;
}

export interface UserFormValues {
	firstName: string;
	lastName: string;
	email: string;
	username: string;
	isActive: boolean;
	password?: string;
	confirmPassword?: string;
	roleIds: string[];
}

interface UserFormProps {
	mode: 'create' | 'edit';
	form: UseFormReturn<UserFormValues>;
	roleOptions: RoleOption[];
	isSubmitting: boolean;
	onSubmit: (values: UserFormValues) => Promise<void> | void;
	headerTitle: string;
	onBack?: () => void;
}

export const UserForm = ({
	mode,
	form,
	roleOptions,
	isSubmitting,
	onSubmit,
	headerTitle,
	onBack
}: UserFormProps) => {
	const { t } = useTranslation('users');
	const firstNameInputRef = useRef<HTMLInputElement>(null);
	const submitLabel =
		mode === 'create' ? t('form.create_user') : t('form.save_changes');

	// Auto-focus first name field when creating a new user
	useEffect(() => {
		if (mode === 'create') {
			setTimeout(() => {
				firstNameInputRef.current?.focus();
			}, 0);
		}
	}, [mode]);

	return (
		<>
			<FormHeaderWithStatus
				title={headerTitle}
				onBack={onBack}
				control={form.control}
				rightContent={
					<FormActionButtons
						isSubmitting={isSubmitting}
						submitLabel={submitLabel}
						validationErrors={form.formState.errors}
						formId="user-form"
					/>
				}
			/>
			<Form {...form}>
			<form id="user-form" onSubmit={form.handleSubmit(onSubmit)} className="space-y-8">
				<Card className="space-y-6">
					<div className="flex flex-col gap-4 mb-4 md:flex-row md:items-center md:justify-between">
					<h2 className="text-lg font-semibold">
						{t('form.profile')}
					</h2>
					<FormSwitch
						control={form.control}
						name="isActive"
						variant="inline"
						label={t('form.status')}
						enabledText={t('form.active')}
						disabledText={t('form.inactive')}
					/>
					</div>

					<Separator className="mb-4" />

					<div className="grid gap-6 md:grid-cols-2">
						<FormField
							control={form.control}
							name="firstName"
							render={({ field }) => (
								<FormItem>
									<FormLabel>
										{t('form.first_name')}
									</FormLabel>
									<FormControl>
										<Input
											{...field}
											ref={firstNameInputRef}
											placeholder={t(
												'form.first_name_placeholder'
											)}
											autoComplete="given-name"
										/>
									</FormControl>
									<FormMessage />
								</FormItem>
							)}
						/>
						<FormField
							control={form.control}
							name="lastName"
							render={({ field }) => (
								<FormItem>
									<FormLabel>{t('form.last_name')}</FormLabel>
									<FormControl>
										<Input
											placeholder={t(
												'form.last_name_placeholder'
											)}
											autoComplete="family-name"
											{...field}
										/>
									</FormControl>
									<FormMessage />
								</FormItem>
							)}
						/>
					</div>

					<div className="grid gap-6 mt-6 md:grid-cols-2">
						<FormField
							control={form.control}
							name="email"
							rules={{
								required: t('form.email_required'),
								pattern: {
									value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/u,
									message: t('form.email_invalid')
								}
							}}
							render={({ field }) => (
								<FormItem>
									<FormLabel>{t('form.email')}</FormLabel>
									<FormControl>
										<Input
											type="email"
											placeholder={t(
												'form.email_placeholder'
											)}
											autoComplete="email"
											{...field}
										/>
									</FormControl>
									<FormMessage />
								</FormItem>
							)}
						/>
						<FormField
							control={form.control}
							name="username"
							rules={{
								required: t('form.username_required'),
								minLength: {
									value: 3,
									message: t('form.username_min_length')
								}
							}}
							render={({ field }) => (
								<FormItem>
									<FormLabel>{t('form.username')}</FormLabel>
									<FormControl>
										<Input
											placeholder={t(
												'form.username_placeholder'
											)}
											autoComplete="username"
											{...field}
										/>
									</FormControl>
									<FormMessage />
								</FormItem>
							)}
						/>
					</div>
				</Card>

				<Card className="space-y-6">
					<h2 className="mb-3 text-lg font-semibold">
						{t('form.access_control')}
					</h2>
					<Separator className="mb-4" />
					<FormField
						control={form.control}
						name="roleIds"
						render={({ field }) => (
							<FormItem>
								<FormLabel>{t('form.roles')}</FormLabel>
								<FormControl>
									<SentryDropdown
										options={roleOptions.map(option => ({
											value: option.value,
											label: option.label,
											category:
												option.description ?? undefined
										}))}
										value={field.value}
										onChange={value => {
											const nextValue = Array.isArray(
												value
											)
												? value
												: [value];
											field.onChange(nextValue);
										}}
										placeholder={
											roleOptions.length
												? t('form.select_role')
												: t('form.no_roles_available')
										}
										multiple
										groupByCategory={false}
										className="w-full"
									/>
								</FormControl>
								<FormDescription>
									{t('form.roles_description')}
								</FormDescription>
								<FormMessage />
							</FormItem>
						)}
					/>
				</Card>

				<Card className="space-y-6">
					<h2 className="mb-3 text-lg font-semibold">
						{t('form.security')}
					</h2>
					<Separator className="mb-4" />{' '}
					<div className="grid gap-6 md:grid-cols-2">
						<FormField
							control={form.control}
							name="password"
							rules={{
								required:
									mode === 'create'
										? t('form.password_required')
										: false,
								minLength: {
									value: mode === 'create' ? 8 : 0,
									message: t('form.password_min_length')
								}
							}}
							render={({ field }) => (
								<FormItem>
									<FormLabel>
										{mode === 'create'
											? t('form.password')
											: t('form.new_password')}
									</FormLabel>
									<FormControl>
										<Input
											type="password"
											autoComplete={
												mode === 'create'
													? 'new-password'
													: 'off'
											}
											placeholder={
												mode === 'create'
													? t(
															'form.password_placeholder'
														)
													: t(
															'form.password_placeholder_edit'
														)
											}
											{...field}
										/>
									</FormControl>
									{mode === 'edit' && (
										<FormDescription>
											{t('form.password_keep_existing')}
										</FormDescription>
									)}
									<FormMessage />
								</FormItem>
							)}
						/>
						<FormField
							control={form.control}
							name="confirmPassword"
							rules={{
								validate: value => {
									const password = form.getValues('password');
									if (
										password &&
										password.length > 0 &&
										password !== value
									) {
										return t('form.passwords_no_match');
									}
									return true;
								}
							}}
							render={({ field }) => (
								<FormItem>
									<FormLabel>
										{t('form.confirm_password')}
									</FormLabel>
									<FormControl>
										<Input
											type="password"
											autoComplete="new-password"
											placeholder={t(
												'form.confirm_password_placeholder'
											)}
											{...field}
										/>
									</FormControl>
									<FormMessage />
								</FormItem>
							)}
						/>
					</div>
				</Card>
			</form>
		</Form>
		</>
	);
};
